import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  BackButton,
  SubmitButton,
  ThinDropdownWithSearch,
  ThinTextField,
} from "../../common/GeneralComponent";
import { useFormik } from "formik";
import * as Yup from "yup";
import { fetchData } from "../../../utils/functions";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Permissions from "../permissions/Permissions";
import Loader from "../../partials/Loader";

const EditRoles = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null); // FIXED
  const [roleDetails, setRoleDetails] = useState({
    role_name: "",
    status: "",
    permissionIds: [],
    groupId: null,
  });
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loader, setLoader] = useState(true);

  // Fetch groups + role details
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await fetchData(
          "groups",
          "GET",
          null,
          {},
          {},
          navigate,
          "/groups",
          dispatch
        );
        setGroups(response?.data || []);
      } catch (error) {
        console.error("Error fetching groups:", error);
      }
    };

    const fetchRoleDetails = async () => {
      try {
        const response = await fetchData(
          `role`,
          "GET",
          null,
          {},
          { id },
          navigate,
          `/role`,
          dispatch
        );
        const roleData = response?.data[0];
        setLoader(false);

        if (roleData) {
          setRoleDetails({
            id,
            groupId: roleData?.role.groupId ?? null,
            role_name: roleData?.role.role_name ?? "",
            status: roleData?.role?.status ?? "",
            permissionIds: roleData?.role?.Permissions?.map((p) => p.id) ?? [],
          });

          setSelectedPermissions(
            roleData?.role?.Permissions?.map((p) => p.id) ?? []
          );

          // set selectedGroup so Permissions can fetch
          setSelectedGroup(roleData?.role.groupId ?? null);
        }
      } catch (error) {
        toast.error(
          error?.message || "An error occurred while fetching the role."
        );
        console.error("Error fetching role details:", error);
      }
    };

    fetchGroups();
    fetchRoleDetails();
  }, [id, navigate, dispatch]);

  // Validation schema
  const validationSchema = Yup.object({
    groupId: Yup.string().required("Group name is required"),
    role_name: Yup.string()
      .required("Role name is required")
      .min(3, "Role name must be at least 3 characters")
      .max(50, "Role name must not exceed 50 characters")
      .matches(/^[^\d]/, "Role name cannot start with a number")
      .matches(
        /^[a-zA-Z0-9 ]*$/,
        "Role name must not contain special characters"
      )
      .test(
        "not-only-numbers",
        "Role name cannot be only numbers",
        (value) => value && !/^\d+$/.test(value)
      ),
    permissionIds: Yup.array()
      .min(1, "At least one permission is required.")
      .required("Permissions are required"),
  });

  const formik = useFormik({
    initialValues: roleDetails,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      const data = {
        id,
        ...values,
        groupId: selectedGroup || values.groupId,
        permissionIds: selectedPermissions,
      };

      try {
        setIsSubmitting(true);
        const response = await fetchData(`role`, "PUT", data);
        toast.success(response.message || "Role updated successfully!");
        navigate("/roles");
      } catch (error) {
        console.error("Error updating role:", error);
        toast.error(
          error?.message || "An error occurred while updating the role."
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Sync Formik with selectedPermissions
  useEffect(() => {
    formik.setFieldValue("permissionIds", selectedPermissions);
  }, [selectedPermissions]);

  // Handle permission changes
  const handlePermissionsChange = (nextIds) => {
    setSelectedPermissions(nextIds);
    formik.setFieldValue("permissionIds", nextIds);
    formik.setFieldTouched("permissionIds", true, false);
  };

  return (
    <div className="w-full my-4 mx-auto">
      {loader && <Loader />}
      <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Edit Roles
        </h1>
        <BackButton to="/roles" text="Back" />
      </div>

      <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4 mb-[7rem]">
        <div className="my-5">
          <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b ">
            <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
              Edit Roles
            </h1>
          </div>

          <form onSubmit={formik.handleSubmit} className="p-4">
            <div className="flex lg:flex-nowrap flex-wrap gap-5 w-full">
              <ThinDropdownWithSearch
                label="Group Name"
                id="group-dropdown"
                options={groups.map((group) => ({
                  value: group.id,
                  label: group.name,
                }))}
                value={formik.values.groupId}
                onChange={(e) => {
                  formik.setFieldValue("groupId", e);
                  setSelectedGroup(e); // FIXED: update selectedGroup for Permissions API
                }}
                onBlur={() => formik.setFieldTouched("groupId", true)}
                error={formik.touched.groupId && Boolean(formik.errors.groupId)}
                helperText={formik.touched.groupId && formik.errors.groupId}
              />

              <ThinTextField
                id="role_name"
                label="Role Name"
                placeholder="Enter Role Name"
                type="text"
                value={formik.values.role_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.role_name && formik.errors.role_name}
                helperText={formik.touched.role_name && formik.errors.role_name}
              />

              <ThinDropdownWithSearch
                label="Status"
                id="status-dropdown"
                options={[
                  { value: 1, label: "Active" },
                  { value: 0, label: "In-Active" },
                ]}
                value={formik.values.status}
                onChange={(value) =>
                  formik.setFieldValue("status", Number(value))
                }
                onBlur={formik.handleBlur}
                error={formik.touched.status && Boolean(formik.errors.status)}
                helperText={formik.touched.status && formik.errors.status}
              />
            </div>

            {/*Permissions block with selectedGroup passed */}
            <Permissions
              selectedGroup={selectedGroup} // <-- now API will work
              selectedPermissions={selectedPermissions}
              setSelectedPermissions={handlePermissionsChange}
            />
            {formik.touched.permissionIds && formik.errors.permissionIds ? (
              <p className="mt-2 text-sm text-red-600">
                {formik.errors.permissionIds}
              </p>
            ) : null}

            <SubmitButton
              type="submit"
              text="Submit"
              isLoading={isSubmitting}
            />
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditRoles;
